import { useState, useEffect } from 'react';

export interface ApiSettings {
  baseUrl: string;
  timeout: number;
  retryAttempts: number;
  enableLogging: boolean;
}

const DEFAULT_SETTINGS: ApiSettings = {
  baseUrl: 'http://localhost:8082/api',
  timeout: 30000,
  retryAttempts: 3,
  enableLogging: true
};

export const useApiSettings = () => {
  const [settings, setSettings] = useState<ApiSettings>(DEFAULT_SETTINGS);
  const [isLoaded, setIsLoaded] = useState(false);

  // Загрузка настроек из localStorage
  useEffect(() => {
    const savedSettings = localStorage.getItem('apiSettings');
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        setSettings(prev => ({ ...prev, ...parsed }));
      } catch (error) {
        console.error('Error loading API settings:', error);
      }
    }
    setIsLoaded(true);
  }, []);

  // Сохранение настроек
  const saveSettings = (newSettings: Partial<ApiSettings>) => {
    const updatedSettings = { ...settings, ...newSettings };
    setSettings(updatedSettings);
    localStorage.setItem('apiSettings', JSON.stringify(updatedSettings));
  };

  // Сброс настроек
  const resetSettings = () => {
    setSettings(DEFAULT_SETTINGS);
    localStorage.setItem('apiSettings', JSON.stringify(DEFAULT_SETTINGS));
  };

  // Получение базового URL для API
  const getBaseUrl = () => settings.baseUrl;

  // Создание конфигурации для fetch запросов
  const createFetchConfig = (endpoint: string, options: RequestInit = {}) => {
    const url = `${settings.baseUrl}${endpoint}`;
    
    const config: RequestInit = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      signal: AbortSignal.timeout(settings.timeout),
    };

    // Добавляем токен авторизации если есть
    const token = localStorage.getItem('token');
    if (token) {
      config.headers = {
        ...config.headers,
        'Authorization': `Bearer ${token}`
      };
    }

    return { url, config };
  };

  // Логирование API запросов
  const logApiRequest = (method: string, url: string, data?: any) => {
    if (settings.enableLogging) {
      console.group(`🌐 API ${method} ${url}`);
      if (data) {
        console.log('Request data:', data);
      }
      console.groupEnd();
    }
  };

  const logApiResponse = (method: string, url: string, response: Response, data?: any) => {
    if (settings.enableLogging) {
      console.group(`✅ API ${method} ${url} - ${response.status}`);
      if (data) {
        console.log('Response data:', data);
      }
      console.groupEnd();
    }
  };

  const logApiError = (method: string, url: string, error: any) => {
    if (settings.enableLogging) {
      console.group(`❌ API ${method} ${url} - Error`);
      console.error('Error details:', error);
      console.groupEnd();
    }
  };

  // Выполнение API запроса с повторными попытками
  const executeApiRequest = async (
    endpoint: string, 
    options: RequestInit = {}, 
    retryCount = 0
  ): Promise<Response> => {
    const { url, config } = createFetchConfig(endpoint, options);
    
    logApiRequest(options.method || 'GET', url, options.body);

    try {
      const response = await fetch(url, config);
      
      if (response.ok) {
        logApiResponse(options.method || 'GET', url, response);
        return response;
      } else {
        // Если ошибка сервера и есть попытки, повторяем
        if (response.status >= 500 && retryCount < settings.retryAttempts) {
          console.warn(`Retrying request (${retryCount + 1}/${settings.retryAttempts})`);
          await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1))); // Экспоненциальная задержка
          return executeApiRequest(endpoint, options, retryCount + 1);
        }
        
        logApiError(options.method || 'GET', url, `HTTP ${response.status}: ${response.statusText}`);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      // Если сетевая ошибка и есть попытки, повторяем
      if (retryCount < settings.retryAttempts) {
        console.warn(`Retrying request due to network error (${retryCount + 1}/${settings.retryAttempts})`);
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
        return executeApiRequest(endpoint, options, retryCount + 1);
      }
      
      logApiError(options.method || 'GET', url, error);
      throw error;
    }
  };

  // Удобные методы для разных типов запросов
  const apiGet = async (endpoint: string) => {
    return executeApiRequest(endpoint, { method: 'GET' });
  };

  const apiPost = async (endpoint: string, data: any) => {
    return executeApiRequest(endpoint, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  };

  const apiPut = async (endpoint: string, data: any) => {
    return executeApiRequest(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  };

  const apiDelete = async (endpoint: string) => {
    return executeApiRequest(endpoint, { method: 'DELETE' });
  };

  // Тестирование соединения
  const testConnection = async (): Promise<boolean> => {
    try {
      const response = await apiGet('/statuses');
      return response.ok;
    } catch (error) {
      return false;
    }
  };

  return {
    // Состояние
    settings,
    isLoaded,
    
    // Методы управления настройками
    saveSettings,
    resetSettings,
    getBaseUrl,
    
    // Методы для API запросов
    executeApiRequest,
    apiGet,
    apiPost,
    apiPut,
    apiDelete,
    testConnection,
    
    // Логирование
    logApiRequest,
    logApiResponse,
    logApiError
  };
}; 